import type {
	HydratedSubmission, SubmissionLossReasons, SubmissionStatus, 
} from "@ca/db/schema";
import type { AdditionalInsured } from "@ca/db/schema/quote";
import type {
	CompFacets, CompRow, CompsQuery, CompSubjectContext,
} from "@ca/types/comps";
import type { MarketPricingPostSchema } from "@ca/types/market_pricing";
import type { Treaty } from "@elysiajs/eden";
import type { MaybeRefOrGetter } from "vue";

import { computed, toValue } from "vue";

import {
	keepPreviousData, type QueryClient, useMutation, useQuery, useQueryClient,
} from "@tanstack/vue-query";

import {
	getNackStatusForReason,
	PilotCompetencyPayload,
	type SubmissionCommentPayload,
	type SubmissionCommentUpdatePayload, 
	type SubmissionIndicationSentiment,
	SubmissionNackBody,
	type SubmissionProcessingStatus,
	SubmissionReminderPayloadSchema,
} from "@ca/types/submissions";

import { client } from "@/api";
import {
	assignedToIds,
	brokerageIds,
	lobFilter,
	newPurchaseFilter,
	statusFilter,
	submissionsDateQuery,
	submissionsFlameGraphQueryKey,
	submissionsPipelineQueryKey,
	submissionsQueryKey,
	targetFilter,
} from "@/composables/submissions";
import { mocoSyncOutcome } from "@/utils/moco-sync";

import { invalidateDashboardQueryKeys } from "./dashboard";
import { PROCESSING_RETRY, processingRefetchInterval } from "./processing";

type AdminSubmissionByIdGet = ReturnType<
	ReturnType<typeof client.api.v1.admin.submissions>["get"]
>;

/** Successful JSON body from GET /api/v1/admin/submissions/:id. */
export type SubmissionByIdResponse = Treaty.Data<AdminSubmissionByIdGet>;

/** Submission row from submission detail (`submission` field when present). */
export type SubmissionDetail = NonNullable<SubmissionByIdResponse["submission"]>;

export const useSubmissions = (enabled: MaybeRefOrGetter<boolean> = true) => useQuery({
	queryFn: async () => {
		const query: Record<string, string | undefined> = {
			...submissionsDateQuery.value,
		};
		if (assignedToIds.value.length > 0) {
			query.assignedToIds = assignedToIds.value.join(",");
		}
		if (brokerageIds.value.length > 0) {
			query.brokerageIds = brokerageIds.value.join(",");
		}
		if (statusFilter.value.length > 0) {
			query.statuses = statusFilter.value.join(",");
		}
		if (lobFilter.value.length > 0) {
			query.lobs = lobFilter.value.join(",");
		}
		if (targetFilter.value) {
			query.target = "true";
		}
		if (newPurchaseFilter.value) {
			query.newPurchase = "true";
		}
		const resp = await client.api.v1.admin.submissions.get({
			query,
		});
		return resp.data?.submissions ?? [];
	},
	queryKey: submissionsQueryKey.value,
	refetchOnMount: false,
	enabled,
});

export const useSubmissionsFlamegraph = () => useQuery({
	queryFn: async () => {
	
		const since = submissionsDateQuery.value.since ??
			new Date(Date.now() - 1000 * 60 * 60 * 24 * 30).toISOString();
		const until = submissionsDateQuery.value.until ?? new Date().toISOString();

		const resp = await client.api.v1.admin.submissions._flamegraph.get({
			query: {
				tzOffset: new Date().getTimezoneOffset(),
				since,
				until,
			},
		});
		return resp.data ?? {
			graphData: [],
			total: 0,
		};
	},
	queryKey: submissionsFlameGraphQueryKey,
	refetchOnMount: false,
});

export const useSubmissionsPipeline = () => useQuery({
	queryFn: async () => {
	
		const since = submissionsDateQuery.value.since ??
			new Date(Date.now() - 1000 * 60 * 60 * 24 * 30).toISOString();
		const until = submissionsDateQuery.value.until ?? new Date().toISOString();

		const resp = await client.api.v1.admin.submissions._pipeline.get({
			query: {
				since,
				until,
			},
		});
		return resp.data ?? {
			counts: [],
			total: 0,
		};
	},
	queryKey: submissionsPipelineQueryKey,
	refetchOnMount: false,
});


export const useSubmissionAttachments = (id: MaybeRefOrGetter<string>) => useQuery({
	queryFn: async () => {
		const resp = await client.api.v1.admin.submissions({ id: toValue(id) }).attachments.get();
		return resp.data?.attachments ?? [];
	},
	queryKey: ["submission-attachments", id],
	refetchOnMount: false,
});


export const useSubmission = (id: MaybeRefOrGetter<string | undefined>) => useQuery({
	queryFn: async (): Promise<SubmissionDetail | undefined> => {
		const resolved = toValue(id);
		if (!resolved) return undefined;
		const resp = await client.api.v1.admin.submissions({ id: resolved }).get();
		return resp.data?.submission;
	},
	queryKey: ["submission", id],
	refetchOnMount: false,
	enabled: () => !!toValue(id),
});

export const useSubmissionAnalysisCitations = (id: MaybeRefOrGetter<string | undefined>) => useQuery({
	queryFn: async () => {
		const resolved = toValue(id);
		if (!resolved) return [];
		const resp = await client.api.v1.citations.get({
			query: {
				referenceId: resolved,
				type: "submission_analysis",
			},
		});
		return resp.data?.citations ?? [];
	},
	queryKey: ["submission-analysis-citations", id],
	refetchOnMount: false,
	enabled: () => !!toValue(id),
});

export const useSubmissionActivities = (id: MaybeRefOrGetter<string>) => useQuery({
	queryFn: async () => {
		const resp = await client.api.v1.admin.submissions({ id: toValue(id) }).activities.get();
		return resp.data?.activities ?? [];
	},
	queryKey: ["submission-activities", id],
	refetchOnMount: false,
});

export const useSubmissionSanctions = (id: MaybeRefOrGetter<string>) => useQuery({
	queryFn: async () => {
		const resp = await client.api.v1.admin.submissions({ id: toValue(id) }).sanctions.get();
		return resp.data?.checks ?? [];
	},
	queryKey: ["submission-sanctions", id],
	refetchOnMount: false,
});

export const useSubmissionComments = (id: MaybeRefOrGetter<string>) => useQuery({
	queryFn: async () => {
		const resp = await client.api.v1.admin.submissions({ id: toValue(id) }).comments.get();
		return resp.data?.comments ?? [];
	},
	queryKey: ["submission-comments", id],
	refetchOnMount: false,
});

export const useSubmissionIndications = (id: MaybeRefOrGetter<string>) => useQuery({
	queryFn: async () => {
		const resp = await client.api.v1.admin.submissions({ id: toValue(id) }).indications.get();
		return resp.data?.indications ?? [];
	},
	queryKey: ["submission-indications", id],
	refetchOnMount: false,
});

export const useSubmissionFlightLogs = (id: MaybeRefOrGetter<string>) => useQuery({
	queryFn: async () => {
		const resp = await client.api.v1.admin.submissions({ id: toValue(id) }).flightlogs.get();
		return resp.data?.flightLogs ?? [];
	},
	queryKey: ["submission-flight-logs", id],
	refetchOnMount: false,
	staleTime: 1000 * 60 * 60,
});

export const useUnderwriters = () => useQuery({
	queryFn: async () => {
		const resp = await client.api.v1.users.underwriters.get();
		return resp.data?.underwriters ?? [];
	},
	queryKey: ["underwriters"],
	refetchOnMount: false,
});

export const useBrokers = () => useQuery({
	queryFn: async () => {
		const resp = await client.api.v1.brokers.get();
		return resp.data?.brokers ?? [];
	},
	queryKey: ["brokers"],
	refetchOnMount: false,
});

export const useBrokerages = () => useQuery({
	queryFn: async () => {
		const resp = await client.api.v1.brokerages.get();
		return resp.data?.brokerages ?? [];
	},
	queryKey: ["brokerages"],
	refetchOnMount: false,
});

export const useAssignSubmission = (id: MaybeRefOrGetter<string>) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (assigneeId: string) => {
			const resp = await client.api.v1.admin.submissions({
				id: toValue(id),
			}).assignee({ assigneeId }).post();
			queryClient.invalidateQueries({ queryKey: ["submission", id] });
			queryClient.invalidateQueries({ queryKey: ["four-eyes"] });
			
			return resp.data;
		},
		mutationKey: ["assign-submission", id],
	});
};

export const useSetSubmissionBroker = (id: MaybeRefOrGetter<string>) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (brokerEmail: string) => {
			const resp = await client.api.v1.admin.submissions({
				id: toValue(id),
			}).broker.post({ brokerEmail });
			queryClient.invalidateQueries({ queryKey: ["submission", id] });
			queryClient.invalidateQueries({ queryKey: submissionsQueryKey.value });
			return resp.data;
		},
		mutationKey: ["set-submission-broker", id],
	});
};

const updateCachedSubmissionStatus = ({
	queryClient, id, status, inceptionDate,
}: {
	queryClient: QueryClient;
	id: MaybeRefOrGetter<string>;
	status?: SubmissionStatus;
	inceptionDate?: string;
}) => {
	queryClient.invalidateQueries({ queryKey: ["submission", id] });
	queryClient.invalidateQueries({ queryKey: ["submission-activities", id] });
	queryClient.invalidateQueries({ queryKey: ["four-eyes"] });
	queryClient.invalidateQueries({ queryKey: submissionsPipelineQueryKey.value });
	queryClient.invalidateQueries({ queryKey: ["renewals"] });
	queryClient.invalidateQueries({ queryKey: ["renewal-volume"] });
	invalidateDashboardQueryKeys(queryClient);
	const sub = queryClient.getQueryData<HydratedSubmission>(["submission", id]);
	const quotes = sub?.quotes ?? [];
	quotes.forEach((quote) => {
		queryClient.invalidateQueries({ queryKey: ["quotes", quote.id] });
	});
	// update changed data locally
	if (status || inceptionDate) {
		queryClient.setQueryData(submissionsQueryKey.value, (old: HydratedSubmission[]) => {
			if (!old?.length) {
				return old;
			}
			return old.map((submission: HydratedSubmission) => {
				if (submission.id === id) {
					return {
						...submission,
						status: status ?? submission.status,
						inceptionDate: inceptionDate ?? submission.inceptionDate,
					};
				}
				return submission;
			});
		});
	}
};

export const useNackSubmission = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (nack: typeof SubmissionNackBody.static) => {
			const resp = await client.api.v1.admin.submissions.nack.post(nack);
			const status = getNackStatusForReason(nack.reason);
			updateCachedSubmissionStatus({
				queryClient,
				id: nack.submissionId,
				status: status ?? undefined,
			});
			const toast = useToast();
			toast.add({
				title: "Submission response sent",
				description: "Response sent to broker",
			});
			return resp.data;
		},
	});
};

export const useUpdateSubmissionStatus = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (payload: { id: MaybeRefOrGetter<string>, status: SubmissionStatus }) => {
			const id = toValue(payload.id);
			const resp = await client.api.v1.admin.submissions({ id }).put({
				status: payload.status,
			});
			if (resp.error) {
				throw new Error((resp.error.value as { message?: string })?.message ?? "Failed to update submission status");
			}
			updateCachedSubmissionStatus({
				queryClient,
				id,
				status: payload.status,
			});
			return resp.data;
		},
	});
};

export const useConfirmQuotePrinted = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (quoteId: string) => {
			const resp = await client.api.v1.quotes({ id: quoteId }).printed.post();
			if (resp.error) {
				throw new Error((resp.error.value as { message?: string })?.message ?? "Failed to record printed quote");
			}
			const result = resp.data;
			if (!result || !("submissionId" in result) || typeof result.submissionId !== "string" || !("markedQuoted" in result) || typeof result.markedQuoted !== "boolean") {
				throw new Error("Failed to record printed quote");
			}
			updateCachedSubmissionStatus({
				queryClient,
				id: result.submissionId,
				status: result.markedQuoted ? "quoted" : undefined,
			});
			queryClient.invalidateQueries({ queryKey: ["quotes", quoteId] });
			return result;
		},
	});
};

export const useUpdateSubmissionTarget = (id: MaybeRefOrGetter<string>) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (target: boolean) => {
			const resp = await client.api.v1.admin.submissions({ id: toValue(id) }).target.put({
				target,
			});
			queryClient.refetchQueries({ queryKey: ["submission", id] });
			queryClient.setQueryData(submissionsQueryKey.value, (old: HydratedSubmission[]) => {
				if (!old?.length) {
					return old;
				}
				return old.map((submission: HydratedSubmission) => {
					if (submission.id === toValue(id)) {
						return {
							...submission,
							target,
						};
					}
					return submission;
				});
			});
			return resp.data;
		},
		mutationKey: ["update-submission-target", id],
	});
};

export const useUpdateSubmissionNewPurchase = (id: MaybeRefOrGetter<string>) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (isNewPurchase: boolean) => {
			const resp = await client.api.v1.admin.submissions({ id: toValue(id) })["new-purchase"].put({
				isNewPurchase,
			});
			queryClient.refetchQueries({ queryKey: ["submission", id] });
			queryClient.setQueryData(submissionsQueryKey.value, (old: HydratedSubmission[]) => {
				if (!old?.length) {
					return old;
				}
				return old.map((submission: HydratedSubmission) => {
					if (submission.id === toValue(id)) {
						return {
							...submission,
							isNewPurchase,
						};
					}
					return submission;
				});
			});
			return resp.data;
		},
		mutationKey: ["update-submission-target", id],
	});
};

export const useUpdateSubmissionAdditionalInterestType = (id: MaybeRefOrGetter<string>) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (payload: {
			index: number;
			interestType: AdditionalInsured["interestType"];
		}) => {
			const resolvedId = toValue(id);
			const resp = await client.api.v1.admin.submissions({ id: resolvedId })["additional-interests"]({
				interestIndex: payload.index.toString(),
			}).put({
				interestType: payload.interestType,
			});
			queryClient.refetchQueries({ queryKey: ["submission"] });
			return resp.data;
		},
		mutationKey: ["update-submission-additional-interest-type", id],
	});
};

const updateFollowupCache = (
	queryClient: QueryClient,
	id: string,
	field: "enableWeekOfFollowup" | "enableDayOfFollowup",
	value: boolean,
) => {
	queryClient.refetchQueries({ queryKey: ["submission", id] });
	queryClient.setQueryData(submissionsQueryKey.value, (old: HydratedSubmission[]) => {
		if (!old?.length) {
			return old;
		}
		return old.map((submission: HydratedSubmission) => {
			if (submission.id === id) {
				return {
					...submission,
					[field]: value,
				};
			}
			return submission;
		});
	});
};

export const useUpdateSubmissionEnableWeekOfFollowup = (id: MaybeRefOrGetter<string>) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (enableWeekOfFollowup: boolean) => {
			const resolvedId = toValue(id);
			const resp = await client.api.v1.admin.submissions({ id: resolvedId })["enable-week-of-followup"].put({
				enableWeekOfFollowup,
			});
			updateFollowupCache(queryClient, resolvedId, "enableWeekOfFollowup", enableWeekOfFollowup);
			return resp.data;
		},
		mutationKey: ["update-submission-enable-week-of-followup", id],
	});
};

export const useUpdateSubmissionEnableDayOfFollowup = (id: MaybeRefOrGetter<string>) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (enableDayOfFollowup: boolean) => {
			const resolvedId = toValue(id);
			const resp = await client.api.v1.admin.submissions({ id: resolvedId })["enable-day-of-followup"].put({
				enableDayOfFollowup,
			});
			updateFollowupCache(queryClient, resolvedId, "enableDayOfFollowup", enableDayOfFollowup);
			return resp.data;
		},
		mutationKey: ["update-submission-enable-day-of-followup", id],
	});
};

export const useUpdateSubmissionIncumbentMarket = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (payload: { id: MaybeRefOrGetter<string>, incumbentMarket: string | null }) => {
			const id = toValue(payload.id);
			const resp = await client.api.v1.admin.submissions({ id: toValue(id) })["incumbent-market"].put({
				incumbentMarket: payload.incumbentMarket,
			});
			queryClient.refetchQueries({ queryKey: ["submission", id] });
			queryClient.setQueryData(submissionsQueryKey.value, (old: HydratedSubmission[]) => {
				if (!old?.length) {
					return old;
				}
				return old.map((submission: HydratedSubmission) => {
					if (submission.id === toValue(id)) {
						return {
							...submission,
							incumbentMarket: payload.incumbentMarket,
						};
					}
					return submission;
				});
			});
			return resp.data;
		},
	});
};

export const useAddSubmissionComment = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (payload: Omit<SubmissionCommentPayload, "email">) => {
			const resp = await client.api.v1.admin.submissions.comments.post(payload);
			queryClient.invalidateQueries({ queryKey: ["submission-comments", payload.submissionId] });
			return resp.data;
		},
	});
};

export const useUpdateSubmissionComment = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (payload:
			Omit<SubmissionCommentUpdatePayload, "email"> & { commentId: string, submissionId: MaybeRefOrGetter<string> }) => {
			const resp = await client.api.v1.admin.submissions.comments({ id: payload.commentId }).put({
				comment: payload.comment,
				approvedStatus: payload.approvedStatus,
			});
			queryClient.invalidateQueries({ queryKey: ["submission-comments", payload.submissionId] });
			return resp.data;
		},
	});
};

export const useUpdateSubmissionLossReason = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (payload: { id: string, lossReason: SubmissionLossReasons }) => {
			await client.api.v1.admin.submissions({ id: payload.id })["loss-reason"].put({ lossReason: payload.lossReason });
			updateCachedSubmissionStatus({
				queryClient,
				id: payload.id,
			});
		},
	});
};

export const useDeleteSubmissionComment = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (payload: {
			id: string;
			submissionId: MaybeRefOrGetter<string>;
		}) => {
			const resp = await client.api.v1.admin.submissions.comments({ id: payload.id }).delete();
			if (resp.status === 200) {
				queryClient.invalidateQueries({ queryKey: ["submission-comments", payload.submissionId] });
			}
		},
	});
};

export const useAddSubmissionIndication = () => {
	const queryClient = useQueryClient();
	const toast = useToast();
	return useMutation({
		mutationFn: async (payload: {
			submissionId: string;
			price: number;
			sentiment: SubmissionIndicationSentiment;
			comment?: string | null;
		}) => {
			const resp = await client.api.v1.admin.submissions.indications.post(payload);
			if (resp.status !== 200) {
				throw new Error("Failed to add indication");
			}
			return resp.data;
		},
		onSuccess: (_, payload) => {
			queryClient.invalidateQueries({ queryKey: ["submission-indications", payload.submissionId] });
			toast.add({
				color: "success",
				title: "Success",
				description: "Indication logged",
			});
		},
		onError: () => {
			toast.add({
				color: "error",
				title: "Error",
				description: "Failed to log indication",
			});
		},
	});
};

export const useUpdateSubmissionIndication = () => {
	const queryClient = useQueryClient();
	const toast = useToast();
	return useMutation({
		mutationFn: async (payload: {
			indicationId: string;
			submissionId: string;
			price: number;
			sentiment: SubmissionIndicationSentiment;
			comment?: string | null;
		}) => {
			const resp = await client.api.v1.admin.submissions.indications({ id: payload.indicationId }).put({
				price: payload.price,
				sentiment: payload.sentiment,
				comment: payload.comment ?? null,
			});
			if (resp.status !== 200) {
				throw new Error("Failed to update indication");
			}
			return resp.data;
		},
		onSuccess: (_, payload) => {
			queryClient.invalidateQueries({ queryKey: ["submission-indications", payload.submissionId] });
			toast.add({
				color: "success",
				title: "Success",
				description: "Indication updated",
			});
		},
		onError: () => {
			toast.add({
				color: "error",
				title: "Error",
				description: "Failed to update indication",
			});
		},
	});
};

export const useDeleteSubmissionIndication = () => {
	const queryClient = useQueryClient();
	const toast = useToast();
	return useMutation({
		mutationFn: async (payload: { indicationId: string, submissionId: string }) => {
			const resp = await client.api.v1.admin.submissions.indications({ id: payload.indicationId }).delete();
			if (resp.status !== 200) {
				throw new Error("Failed to delete indication");
			}
			return resp.data;
		},
		onSuccess: (_, payload) => {
			queryClient.invalidateQueries({ queryKey: ["submission-indications", payload.submissionId] });
			toast.add({
				color: "success",
				title: "Success",
				description: "Indication deleted",
			});
		},
		onError: () => {
			toast.add({
				color: "error",
				title: "Error",
				description: "Failed to delete indication",
			});
		},
	});
};

export const useReviewSubmissionIndication = () => {
	const queryClient = useQueryClient();
	const toast = useToast();
	return useMutation({
		mutationFn: async (payload: {
			indicationId: string;
			reason?: string | null;
			status: "approved" | "rejected";
			submissionId: string;
		}) => {
			const resp = await client.api.v1.admin.submissions.indications({ id: payload.indicationId }).review.post({
				reason: payload.reason ?? null,
				status: payload.status,
			});
			if (resp.status !== 200) {
				throw new Error("Failed to review indication");
			}
			return resp.data;
		},
		onSuccess: (_, payload) => {
			queryClient.invalidateQueries({ queryKey: ["submission-indications", payload.submissionId] });
			invalidateDashboardQueryKeys(queryClient);
			if (payload.status === "approved") {
				updateCachedSubmissionStatus({
					queryClient,
					id: payload.submissionId,
					status: "indicated",
				});
			}
			toast.add({
				color: "success",
				title: "Success",
				description: payload.status === "approved" ? "Indication approved" : "Indication rejected",
			});
		},
		onError: () => {
			toast.add({
				color: "error",
				title: "Error",
				description: "Failed to review indication",
			});
		},
	});
};


export const useUpdateSubmissionInceptionDate = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async ({
			id,
			inceptionDate,
		}: {
			id: MaybeRefOrGetter<string>;
			inceptionDate: string;
		}) => {
			const resp = await client.api.v1.admin.submissions({ id: toValue(id) })["inception-date"].put({ inceptionDate });
			updateCachedSubmissionStatus({
				queryClient,
				id,
				inceptionDate,
			});
			const toast = useToast();
			if (resp.status === 200) {
				toast.add({
					color: "success",
					title: "Success",
					description: "Inception date updated",
					duration: 1200,
				});
			} else {
				toast.add({
					color: "error",
					title: "Error",
					description: "Failed to update inception date",
				});
			}
		},
	});
};

export const useUpdateSubmissionDemoedOn = () => {
	const queryClient = useQueryClient();
	const toast = useToast();
	return useMutation({
		mutationFn: async ({
			id,
			demoedOn,
		}: {
			id: MaybeRefOrGetter<string>;
			demoedOn: string | null;
		}) => {
			const resp = await client.api.v1.admin.submissions({ id: toValue(id) })["demoed-on"].put({ demoedOn });

			if (resp.status === 200) {
				toast.add({
					color: "success",
					title: "Success",
					description: "Demo date updated",
					duration: 1200,
				});
				queryClient.invalidateQueries({ queryKey: ["submission", id] });
			} else {
				toast.add({
					color: "error",
					title: "Error",
					description: "Failed to update demo date",
				});
			}
		},
	});
};
export const useMarketPricing = (id: MaybeRefOrGetter<string>) => useQuery({
	queryFn: async () => {
		const resp = await client.api.v1.admin.submissions({ id: toValue(id) })["market-pricing"].get();
		return resp.data?.marketPricing ?? [];
	},
	queryKey: ["market-pricing", id],
	refetchOnMount: false,
});

export const useAddMarketPricing = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (payload: typeof MarketPricingPostSchema.static) => {
			const resp = await client.api.v1.admin.submissions["market-pricing"].post(payload);
			if (resp.status === 200) {
				queryClient.invalidateQueries({ queryKey: ["market-pricing", payload.submissionId] });
				const toast = useToast();
				toast.add({
					color: "success",
					title: "Success",
					description: "Market pricing added",
				});
			} else {
				const toast = useToast();
				toast.add({
					color: "error",
					title: "Error",
					description: "Failed to add market pricing",
				});
			}
			return resp.data;
		},
	});
};

export const useSubmissionConflicts = (id: MaybeRefOrGetter<string>) => useQuery({
	queryFn: async () => {
		const resp = await client.api.v1.admin.submissions({ id: toValue(id) }).conflicts.get();
		return resp.data?.conflicts ?? [];
	},
	queryKey: ["submission-conflicts", id],
	refetchOnMount: false,
});

export type PendingDecisionJob = {
	status: "failed" | "running" | "scheduled";
};

export const usePendingDecisionJob = (id: MaybeRefOrGetter<string>) => useQuery({
	queryFn: async () => {
		const resp = await client.api.v1.admin.submissions({ id: toValue(id) })["decision-job"].get();
		return (resp.data?.job ?? null) as null | PendingDecisionJob;
	},
	queryKey: ["submission-decision-job", id],
});

export const useSubmissionProcessing = (id: MaybeRefOrGetter<string>) => useQuery({
	queryFn: async () => {
		const resp = await client.api.v1.admin.submissions({ id: toValue(id) }).processing.get();
		if (resp.error) {
			throw resp.error;
		}
		return (resp.data?.status ?? null) as null | SubmissionProcessingStatus;
	},
	enabled: () => Boolean(toValue(id)),
	queryKey: ["submission-processing", id],
	refetchInterval: processingRefetchInterval,
	retry: PROCESSING_RETRY,
});

export type UnderwritingStepWithActions = {
	id: string;
	title: string | null;
	description: string;
	rationale: string | null;
	relevant: boolean;
	sequence: number;
	startedAt: string;
	endedAt: string;
	actions: Array<{
		id: string;
		actionKind: string;
		source: string;
		payload: Record<string, unknown>;
		stepRelevant: boolean | null;
		createdAt: string;
		performedBy: string | null;
		deleted: boolean;
		deletionRationale: string | null;
		impactCategory: string | null;
	}>;
};

export type UngroupedAction = {
	id: string;
	submissionId: string;
	actionKind: string;
	source: string;
	payload: Record<string, unknown>;
	createdAt: string;
	performedBy: string | null;
	impactCategory: string | null;
};

export const useSubmissionUnderwritingSteps = (id: MaybeRefOrGetter<string>) => useQuery({
	queryFn: async () => {
		const resp = await client.api.v1.admin.submissions({ id: toValue(id) })["underwriting-steps"].get();
		return resp.data?.steps ?? [];
	},
	queryKey: ["submission-underwriting-steps", id],
	refetchOnMount: false,
});

export const useSubmissionUngroupedActions = (id: MaybeRefOrGetter<string>) => useQuery({
	queryFn: async () => {
		const resp = await client.api.v1.admin.submissions({ id: toValue(id) })["ungrouped-actions"].get();
		return resp.data?.actions ?? [];
	},
	queryKey: ["submission-ungrouped-actions", id],
	refetchOnMount: false,
});

export const useRunUnderwritingGrouping = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (submissionId: string) => {
			const resp = await client.api.v1.admin.submissions({ id: submissionId })["run-underwriting-grouping"].post();
			if (resp.status !== 200 || resp.data == null) {
				throw new Error((resp.error?.value as { message?: string })?.message ?? "Failed to run underwriting grouping");
			}
			const data = resp.data as { stepsCreated: number };
			queryClient.invalidateQueries({ queryKey: ["submission-underwriting-steps", submissionId] });
			queryClient.invalidateQueries({ queryKey: ["submission-ungrouped-actions", submissionId] });
			return data;
		},
		mutationKey: ["run-underwriting-grouping"],
	});
};

export const useSyncSubmissionToMoco = () => {
	const toast = useToast();
	return useMutation({
		mutationFn: async (submissionId: string) => mocoSyncOutcome(await client.api.v1.admin.submissions({ id: submissionId })["moco-sync"].post()),
		onSuccess: (description) => {
			toast.add({
				description, color: "success", duration: 1500,
			});
		},
		onError: (error) => {
			const description = error instanceof Error ? error.message : "Failed to sync to Moco";
			toast.add({
				description,
				color: "error",
				duration: 8000,
			});
		},
	});
};

export const useGenerateSubmissionConsiderations = (id: MaybeRefOrGetter<string>) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async () => {
			const submissionId = toValue(id);
			const resp = await client.api.v1.admin.submissions({ id: submissionId }).considerations.generate.post();
			if (resp.status !== 200 || resp.data == null) {
				throw new Error((resp.error?.value as { message?: string })?.message ?? "Failed to generate considerations");
			}
			return resp.data;
		},
		onSuccess: (_, __, ___) => {
			queryClient.invalidateQueries({ queryKey: ["submission", id] });
			queryClient.invalidateQueries({ queryKey: ["submission-analysis-citations", id] });
		},
		mutationKey: ["generate-submission-considerations", id],
	});
};

export const useUpdateUnderwritingStepRelevant = (submissionId: MaybeRefOrGetter<string>) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async ({ stepId, relevant }: { stepId: string; relevant: boolean }) => {
			const id = toValue(submissionId);
			const resp = await client.api.v1.admin.submissions({ id })["underwriting-steps"]({
				stepId,
			}).patch({ relevant });
			if (resp.status !== 200) {
				throw new Error((resp.error?.value as { message?: string })?.message ?? "Failed to update step");
			}
			return { stepId, relevant };
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["submission-underwriting-steps", submissionId] });
		},
		mutationKey: ["update-underwriting-step-relevant", submissionId],
	});
};

export const useUpdateSubmissionDecisionBlurb = (id: MaybeRefOrGetter<string>) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (underwriterDecisionBlurb: string | null) => {
			const resp = await client.api.v1.admin.submissions({ id: toValue(id) })["decision-rationale"].put({
				underwriterDecisionBlurb,
			});
			return resp.data;
		},
		onSuccess: (_, __, ___) => {
			queryClient.invalidateQueries({ queryKey: ["submission", id] });
		},
		mutationKey: ["submission-decision-rationale", id],
	});
};

export const useUpdateSubmissionRiskRating = (id: MaybeRefOrGetter<string>) => {
	const queryClient = useQueryClient();
	const toast = useToast();
	return useMutation({
		mutationFn: async (rating: "low" | "medium" | "high") => {
			const resp = await client.api.v1.admin.submissions({ id: toValue(id) })["risk-rating"].put({
				rating,
			});
			if (resp.status !== 200) {
				throw new Error("Failed to update risk rating");
			}
			return resp.data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["submission", id] });
			toast.add({
				color: "success",
				title: "Risk rating updated",
				duration: 1200,
			});
		},
		onError: () => {
			toast.add({
				color: "error",
				title: "Error",
				description: "Failed to update risk rating",
			});
		},
		mutationKey: ["submission-risk-rating", id],
	});
};

export const useDismissBORWarning = () => {
	const queryClient = useQueryClient();
	const toast = useToast();
	return useMutation({
		mutationFn: async (submissionId: MaybeRefOrGetter<string>) => {
			const resp = await client.api.v1.admin.submissions.bor.dismiss.post({ submissionId: toValue(submissionId) });
			queryClient.invalidateQueries({ queryKey: ["submission-conflicts", submissionId] });
			queryClient.invalidateQueries({ queryKey: ["submission-activities", submissionId] });
     
			return resp.data;
		},
		onSuccess: () => {
			toast.add({
				color: "success",
				title: "Success",
				description: "BOR warning dismissed",
			});
		},
		onError: () => {
			toast.add({
				color: "error",
				title: "Error",
				description: "Failed to dismiss BOR warning",
			});
		},
	});
};


export const useSubmissionThreads = (id: MaybeRefOrGetter<string>) => useQuery({
	queryFn: async () => {
		const resp = await client.api.v1.admin.submissions({ id: toValue(id) }).threads.get();
		return resp.data?.threads ?? [];
	},
	queryKey: ["submission-thread", id],
	refetchOnMount: false,
});

export const useUploadSubmissionAttachment = () => {
	const queryClient = useQueryClient();
	const toast = useToast();
	return useMutation({
		mutationFn: async (payload: {
			submissionId: string;
			file: File;
		}) => {
			const formData = new FormData();
			formData.append("file", payload.file);

			const resp = await client.api.v1.admin.submissions({ id: payload.submissionId }).attachments.post({
				file: payload.file,
			});

			if (resp.status === 200) {
				queryClient.invalidateQueries({ queryKey: ["submission-attachments", payload.submissionId] });
				toast.add({
					color: "success",
					title: "Success",
					description: "File uploaded successfully",
				});
			} else {
				toast.add({
					color: "error",
					title: "Error",
					description: "Failed to upload file",
				});
			}
			return resp.data;
		},
	});
};

export const useSubmissionReminders = (id: MaybeRefOrGetter<string>) => useQuery({
	queryFn: async () => {
		const resp = await client.api.v1.submissions({ id: toValue(id) }).reminders.get();
		return resp.data?.reminders ?? [];
	},
	queryKey: ["submission-reminders", id],
	initialData: [],
	refetchOnMount: true,
});

const setLatestReminderInCachedList = ({
	id,
	reminderDate,
	queryClient,
}: {
	id: string;
	reminderDate: string | null;
	queryClient: QueryClient;
}) => {
	queryClient.setQueryData(submissionsQueryKey.value, (old: HydratedSubmission[]) => {
		if (!old?.length) {
			return old;
		}
		return old.map((submission: HydratedSubmission) => {
			if (submission.id === id) {
				return {
					...submission,
					nextReminderDate: reminderDate,
				};
			}
			return submission;
		});
	});
};

export const useCreateSubmissionReminder = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (payload: typeof SubmissionReminderPayloadSchema.static & { submissionId: string }) => {
			const resp = await client.api.v1.submissions({ id: payload.submissionId }).reminders.post({ reminderDate: payload.reminderDate, channels: payload.channels });
			const current = queryClient.getQueryData<Array<{ id: string, reminderDate: string, sentAt: string | null }>>(["submission-reminders", payload.submissionId]) ?? [];
			const now = new Date();
			let nextDate = payload.reminderDate;
			for (const reminder of current) {
				if (new Date(reminder.reminderDate) < new Date(nextDate) && new Date(reminder.reminderDate) >= now) {
					nextDate = reminder.reminderDate;
				}
			}
			queryClient.invalidateQueries({ queryKey: ["submission-reminders", payload.submissionId] });
			setLatestReminderInCachedList({
				id: payload.submissionId,
				reminderDate: nextDate,
				queryClient,
			});
			return resp.data;
		},
	});
};

export const useDeleteSubmissionReminder = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (payload: { submissionId: string, reminderId: string }) => {
			const resp = await client.api.v1.submissions({ id: payload.submissionId }).reminders({ reminderId: payload.reminderId }).delete();
			const current = queryClient.getQueryData<Array<{ id: string, reminderDate: string, sentAt: string | null }>>(["submission-reminders", payload.submissionId]) ?? [];
			const now = new Date();
			// Filter to match server-side logic: only unsent reminders with future dates
			const eligibleReminders = current.filter((reminder) =>
				reminder.id !== payload.reminderId &&
				reminder.sentAt === null &&
				new Date(reminder.reminderDate) >= now,
			);
			// Find the earliest (next) reminder
			let nextDate: string | undefined = undefined;
			for (const reminder of eligibleReminders) {
				if (!nextDate || new Date(reminder.reminderDate) < new Date(nextDate)) {
					nextDate = reminder.reminderDate;
				}
			}
			setLatestReminderInCachedList({
				id: payload.submissionId,
				reminderDate: nextDate ?? null,
				queryClient,
			});
			queryClient.invalidateQueries({ queryKey: ["submission-reminders", payload.submissionId] });
			
			return resp.data;
		},
	});
};

export const useEditSubmissionReminder = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (payload: typeof SubmissionReminderPayloadSchema.static & { reminderId: string, submissionId: string }) => {
			const resp = await client.api.v1.submissions({
				id: payload.submissionId,
			}).reminders({
				reminderId: payload.reminderId,
			}).put({
				reminderDate: payload.reminderDate,
				channels: payload.channels,
			});
			queryClient.invalidateQueries({ queryKey: ["submission-reminders", payload.submissionId] });
			// get the new next update
			const current = queryClient.getQueryData<Array<{ id: string, reminderDate: string, sentAt: string | null }>>(["submission-reminders", payload.submissionId]) ?? [];
			let nextDate: string | undefined = undefined;
			const now = new Date();
			for (const reminder of current) {
				const rDate = reminder.id === payload.reminderId ? payload.reminderDate : reminder.reminderDate;
				if (!nextDate || (new Date(rDate) < new Date(nextDate) && new Date(rDate) >= now)) {
					nextDate = rDate;
				}
			}
			setLatestReminderInCachedList({
				id: payload.submissionId,
				reminderDate: nextDate ?? null,
				queryClient,
			});
			return resp.data;
		},
	});
};

export const useUpdateSubmissionOPAs = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (payload: { id: string, opas: string[] }) => {
			const resp = await client.api.v1.admin.submissions({ id: payload.id }).opas.put({
				opas: payload.opas,
			});
			queryClient.refetchQueries({ queryKey: ["submission", payload.id] });
		
			return resp.data;
		},
		onSuccess: () => {
			const toast = useToast();
			toast.add({
				color: "success",
				title: "Success",
				description: "OPAS updated",
			});
		},
		onError: () => {
			const toast = useToast();
			toast.add({
				color: "error",
				title: "Error",
				description: "Failed to update OPAS",
			});
		},
	});
};

export const usePilotRating = (id: MaybeRefOrGetter<string>) => useQuery({
	queryFn: async () => {
		const resp = await client.api.v1.admin.submissions({ id: toValue(id) })["pilot-rating"].get();
		return resp.data?.rating ?? null;
	},
	queryKey: ["submission-pilot-rating", id],
	refetchOnMount: false,
});

export const useBackfillPilotRating = () => {
	const queryClient = useQueryClient();
	const toast = useToast();
	return useMutation({
		mutationFn: async (submissionId: string) => {
			const resp = await client.api.v1.admin.submissions({ id: submissionId })["pilot-rating"].backfill.post();
			if (resp.status !== 200 || resp.data == null) {
				throw new Error((resp.error?.value as { message?: string })?.message ?? "Failed to generate pilot rating");
			}
			return resp.data;
		},
		onSuccess: (_, submissionId) => {
			queryClient.invalidateQueries({ queryKey: ["submission-pilot-rating", submissionId] });
			toast.add({
				color: "success",
				title: "Success",
				description: "Pilot rating generated",
			});
		},
		onError: (error: Error) => {
			toast.add({
				color: "error",
				title: "Error",
				description: error.message,
			});
		},
	});
};

export const useUpsertPilotRating = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (payload: typeof PilotCompetencyPayload.static) => {
			const resp = await client.api.v1.admin["pilot-rating"].post(payload);
			if (resp.status !== 200 || resp.data == null) {
				throw new Error((resp.error?.value as { message?: string })?.message ?? "Failed to save pilot rating");
			}
			return resp.data;
		},
		onSuccess: (_, payload) => {
			queryClient.invalidateQueries({ queryKey: ["submission-pilot-rating", payload.submissionId] });
			const toast = useToast();
			toast.add({
				color: "success",
				title: "Success",
				description: "Pilot rating saved",
			});
		},
		onError: () => {
			const toast = useToast();
			toast.add({
				color: "error",
				title: "Error",
				description: "Failed to save pilot rating",
			});
		},
	});
};

export const useDeletePilotRating = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (submissionId: string) => {
			const resp = await client.api.v1.admin.submissions({ id: submissionId })["pilot-rating"].delete();
			if (resp.status !== 200 || resp.data == null) {
				throw new Error((resp.error?.value as { message?: string })?.message ?? "Failed to remove pilot rating");
			}
			return resp.data;
		},
		onSuccess: (_, submissionId) => {
			queryClient.invalidateQueries({ queryKey: ["submission-pilot-rating", submissionId] });
			const toast = useToast();
			toast.add({
				color: "success",
				title: "Success",
				description: "Pilot rating removed",
			});
		},
		onError: () => {
			const toast = useToast();
			toast.add({
				color: "error",
				title: "Error",
				description: "Failed to remove pilot rating",
			});
		},
	});
};

export interface SubmissionCompsQueryResponse {
	comps: Array<CompRow>;
	totalCount: number;
	windowMonths: number | null;
	limit: number;
	facets: CompFacets;
	subject: CompSubjectContext;
}

const compsQueryKeyPart = (query: CompsQuery): string => JSON.stringify({
	filters: [...(query.filters ?? [])]
		.sort((a, b) => a.field.localeCompare(b.field)),
	windowMonths: query.windowMonths === undefined ? "default" : query.windowMonths,
	limit: query.limit ?? null,
	target: query.target ?? null,
});

export const useSubmissionCompsQuery = (
	id: MaybeRefOrGetter<string>,
	query: MaybeRefOrGetter<CompsQuery>,
	enabled: MaybeRefOrGetter<boolean> = true,
) => useQuery({
	queryFn: async (): Promise<SubmissionCompsQueryResponse> => {
		const resp = await client.api.v1.submissions({ id: toValue(id) }).comps.query.post(toValue(query));
		if (resp.status !== 200 || resp.data == null) {
			throw new Error((resp.error?.value as { message?: string })?.message ?? "Failed to load comps");
		}
		return resp.data as SubmissionCompsQueryResponse;
	},
	queryKey: ["submission-comps", id, computed(() => compsQueryKeyPart(toValue(query)))],
	refetchOnMount: false,
	refetchOnWindowFocus: false,
	placeholderData: keepPreviousData,
	enabled,
});
